// Conditional request evaluation (browserdav-nephele-parity Phase 2).
//
// Mirrors Nephele's Method.js ordering (auth → resolve resource/validators →
// conditionals → handler) and RFC 7232 §6 precedence: If-Match, else
// If-Unmodified-Since; then If-None-Match, else If-Modified-Since (reads
// only). Replaces the Phase 0 interim behavior of rejecting these headers
// with 501.

function parseEtagList(headerValue) {
    return String(headerValue)
        .split(',')
        .map((raw) => raw.trim())
        .filter(Boolean)
        .map((raw) => ({
            weak: raw.startsWith('W/'),
            tag: raw.replace(/^W\//, '').replace(/^"|"$/g, ''),
        }))
}

// If-Match uses the strong comparison: weak validators never match.
function strongMatch(headerValue, validators) {
    if (headerValue.trim() === '*') return validators.exists
    if (!validators.exists || !validators.etag) return false
    return parseEtagList(headerValue).some((e) => !e.weak && e.tag === validators.etag)
}

// If-None-Match uses the weak comparison: W/ prefixes are ignored.
function weakMatch(headerValue, validators) {
    if (headerValue.trim() === '*') return validators.exists
    if (!validators.exists || !validators.etag) return false
    return parseEtagList(headerValue).some((e) => e.tag === validators.etag)
}

// HTTP dates carry second resolution; compare at second granularity.
function modifiedAfter(lastModifiedMs, httpDate) {
    const since = Date.parse(httpDate)
    if (isNaN(since)) return null // unparseable date → ignore the header
    return Math.floor(lastModifiedMs / 1000) * 1000 > since
}

/**
 * Evaluate conditional headers against the target's validators.
 *
 * @param method   uppercase HTTP method
 * @param headers  lowercase-keyed request headers
 * @param v        { exists, etag?, lastModified? } from _resolveValidators
 * @returns        null when all preconditions pass, else a response object
 *                 { status, headers?, body } (304 for reads, 412 otherwise)
 */
export function checkConditionalHeaders(method, headers, v) {
    const isRead = method === 'GET' || method === 'HEAD'
    const notModified = () => ({
        status: 304,
        headers: v.etag ? { ETag: `"${v.etag}"` } : {},
        body: '',
    })
    const preconditionFailed = (header) => ({
        status: 412,
        body: `Precondition Failed: ${header}`,
    })

    const ifMatch = headers['if-match']
    const ifUnmodifiedSince = headers['if-unmodified-since']
    const ifNoneMatch = headers['if-none-match']
    const ifModifiedSince = headers['if-modified-since']

    if (ifMatch !== undefined) {
        if (!strongMatch(ifMatch, v)) return preconditionFailed('If-Match')
    } else if (ifUnmodifiedSince !== undefined && v.exists && v.lastModified != null) {
        if (modifiedAfter(v.lastModified, ifUnmodifiedSince) === true) {
            return preconditionFailed('If-Unmodified-Since')
        }
    }

    if (ifNoneMatch !== undefined) {
        if (weakMatch(ifNoneMatch, v)) {
            return isRead ? notModified() : preconditionFailed('If-None-Match')
        }
    } else if (ifModifiedSince !== undefined && isRead && v.exists && v.lastModified != null) {
        if (modifiedAfter(v.lastModified, ifModifiedSince) === false) {
            return notModified()
        }
    }

    return null
}

export function hasConditionalHeaders(headers) {
    return (
        headers['if-match'] !== undefined ||
        headers['if-none-match'] !== undefined ||
        headers['if-modified-since'] !== undefined ||
        headers['if-unmodified-since'] !== undefined
    )
}
